import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, X, ChevronDown, Menu, Download } from 'lucide-react';
import { getGiveAwayContent } from 'Services/allApi';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const GiveawayParticipants = () => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showItemsDropdown, setShowItemsDropdown] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  const itemsPerPageOptions = [5, 10, 25, 50, 100];

  useEffect(() => {
    fetchParticipants(currentPage);
  }, [currentPage, itemsPerPage]);

  const fetchParticipants = async (page) => {
    try {
      setLoading(true);
      const response = await getGiveAwayContent(page, searchTerm, itemsPerPage);
      
      if (response.error) {
        throw new Error(response.message);
      }
      
      setParticipants(response.results);
      setTotalPages(Math.ceil(response.count / itemsPerPage));
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchParticipants(1);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
    setShowItemsDropdown(false);
  };

  const openParticipantDetails = (participant) => {
    setSelectedParticipant(participant);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedParticipant(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Function to export participants as PDF
  const exportToPDF = async () => {
    try {
      setExportLoading(true);
      
      // Create new PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Add title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Giveaway Participants', pageWidth / 2, 15, { align: 'center' });
      
      // Add export date
      const exportDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Exported on: ${exportDate}`, pageWidth - 15, 10, { align: 'right' });
      
      // Create table with auto columns
      const tableColumn = ["Name", "Email", "College", "Course", "Graduation", "Date Added"];
      const tableRows = [];
      
      // Add data to rows
      participants.forEach(participant => {
        const participantData = [
          `${participant.first_name} ${participant.last_name}`,
          participant.email,
          participant.college,
          participant.course,
          participant.graduation_year,
          formatDate(participant.created_at)
        ];
        tableRows.push(participantData);
      });
      
      // Add table to PDF using the plugin
      const finalY = autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 25,
        headStyles: { fillColor: [102, 51, 153], textColor: 255 }, // Purple header
        alternateRowStyles: { fillColor: [240, 240, 250] },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 'auto' },
          4: { cellWidth: 'auto' },
          5: { cellWidth: 'auto' }
        },
        didDrawPage: (data) => {
          // This function runs on every page
        },
      });
      
      // Get the final Y position directly from the return value
      const tableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 
                        finalY ? finalY : 150; // Use finalY or fallback to doc.lastAutoTable or provide default
      
      // Add page info
      const pageInfo = `Page ${currentPage} of ${totalPages} - ${participants.length} participants`;
      doc.setFontSize(10);
      doc.text(pageInfo, pageWidth / 2, tableEndY + 10, { align: 'center' });
      
      // Add search filter info if applicable
      if (searchTerm) {
        doc.text(`Filter: "${searchTerm}"`, 15, tableEndY + 15);
      }
      
      // Save PDF
      doc.save('giveaway-participants.pdf');
      setExportLoading(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setExportLoading(false);
      alert('Failed to export PDF. Please try again.');
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showItemsDropdown && !event.target.closest('.items-dropdown-container')) {
        setShowItemsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showItemsDropdown]);

  if (loading && participants.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg font-medium text-gray-600">Loading participants...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-md">
        <p className="font-medium">Error loading participants: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Giveaway Participants</h1>
          {participants.length > 0 && (
            <button
              onClick={exportToPDF}
              disabled={exportLoading}
              className="mt-2 sm:mt-0 flex items-center px-3 py-1 text-xs sm:text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
            >
              {exportLoading ? (
                <span>Exporting...</span>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  <span>Export PDF</span>
                </>
              )}
            </button>
          )}
        </div>
        <form onSubmit={handleSearch} className="relative w-full sm:w-64 md:w-80">
          <div className="relative flex items-center">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <input
              type="text"
              placeholder="Search participants..."
              className="w-full pl-10 pr-20 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="submit"
              className="absolute right-0 h-full px-3 sm:px-4 bg-purple-600 text-white text-sm rounded-r-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {participants.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 text-lg">No participants found.</p>
        </div>
      ) : (
        <>
          {/* Mobile view: Card layout */}
          <div className="md:hidden space-y-4">
            {participants.map((participant) => (
              <div 
                key={participant.id}
                className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => openParticipantDetails(participant)}
              >
                <div className="font-medium text-gray-900 mb-1">
                  {participant.first_name} {participant.last_name}
                </div>
                <div className="text-sm text-gray-600 mb-1">{participant.email}</div>
                <div className="text-sm text-gray-500">
                  <span className="inline-block mr-2">{participant.college}</span>
                  <span className="inline-block">{formatDate(participant.created_at)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop view: Table layout */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Email</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">College</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Course</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Graduation</th>
                  <th className="py-3 px-2 lg:px-4 text-left text-xs lg:text-sm font-medium text-gray-600 uppercase tracking-wider">Date Added</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {participants.map((participant) => (
                  <tr 
                    key={participant.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => openParticipantDetails(participant)}
                  >
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 text-sm lg:text-base">
                        {participant.first_name} {participant.last_name}
                      </div>
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-600">
                      {participant.email}
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-600">
                      {participant.college}
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-600">
                      {participant.course}
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-600">
                      {participant.graduation_year}
                    </td>
                    <td className="px-2 lg:px-4 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-600">
                      {formatDate(participant.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6 gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="text-xs sm:text-sm text-gray-600">
                Showing page {currentPage} of {totalPages}
              </div>
              <div className="relative items-dropdown-container">
                <div 
                  className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-2 border border-gray-300 rounded-md cursor-pointer text-xs sm:text-sm"
                  onClick={() => setShowItemsDropdown(!showItemsDropdown)}
                >
                  <span className="text-gray-700">{itemsPerPage} per page</span>
                  <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                </div>
                {showItemsDropdown && (
                  <div className="absolute z-10 mt-1 w-28 sm:w-36 bg-white shadow-lg rounded-md py-1 border border-gray-200">
                    {itemsPerPageOptions.map((option) => (
                      <div 
                        key={option} 
                        className="px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleItemsPerPageChange(option)}
                      >
                        {option} per page
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-2 text-xs sm:text-sm">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center px-2 py-1 sm:px-3 sm:py-1 rounded ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`flex items-center px-2 py-1 sm:px-3 sm:py-1 rounded ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Participant Details Modal */}
      {showModal && selectedParticipant && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity" 
              onClick={closeModal}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full mx-4 sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Participant Details
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-500">First Name</label>
                      <div className="mt-1 text-sm sm:text-base text-gray-900">{selectedParticipant.first_name}</div>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-500">Last Name</label>
                      <div className="mt-1 text-sm sm:text-base text-gray-900">{selectedParticipant.last_name}</div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500">Email</label>
                    <div className="mt-1 text-sm sm:text-base text-gray-900 break-words">{selectedParticipant.email}</div>
                  </div>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500">Phone</label>
                    <div className="mt-1 text-sm sm:text-base text-gray-900">{selectedParticipant.phone}</div>
                  </div>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500">College</label>
                    <div className="mt-1 text-sm sm:text-base text-gray-900 break-words">{selectedParticipant.college}</div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-500">Course</label>
                      <div className="mt-1 text-sm sm:text-base text-gray-900">{selectedParticipant.course}</div>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-500">Graduation Year</label>
                      <div className="mt-1 text-sm sm:text-base text-gray-900">{selectedParticipant.graduation_year}</div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500">Registration Date</label>
                    <div className="mt-1 text-sm sm:text-base text-gray-900">{formatDate(selectedParticipant.created_at)}</div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiveawayParticipants;